'use client';

import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { FreeMode, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper';
import { useTranslations } from 'next-intl';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { PlayIcon } from '@heroicons/react/20/solid';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { ImageSize } from '@/ui/utils/getOptimizedImageUrl';
import { resizedImage } from '@/integrations/crystallize/helpers/crystallize-image-helper';
import {
  ImageFragment,
  VideoFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import './product-image-gallery.css';
import { GalleryVideo } from './gallery-video';
import { GalleryImageSlide } from './gallery-image-slide';

export interface ProductImageGalleryProps {
  galleryImages?: ImageFragment[];
  videos?: VideoFragment[];
}

type GalleryMedia =
  | { kind: 'image'; key: string; image: ImageFragment }
  | { kind: 'video'; key: string; video: VideoFragment };

const buildMedia = (images: ImageFragment[], videos: VideoFragment[]): GalleryMedia[] => [
  ...images.map((image): GalleryMedia => ({ kind: 'image', key: image.key, image })),
  ...videos.map((video): GalleryMedia => ({ kind: 'video', key: video.id, video })),
];

export function ProductImageGallery({ galleryImages, videos }: Readonly<ProductImageGalleryProps>) {
  const t = useTranslations('ProductChooserWrapper');
  const [mainSwiper, setMainSwiper] = useState<SwiperClass | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Callback refs (state) rather than useRef: the nav buttons render after the Swiper, so a useRef
  // would still be null when Swiper reads it. State makes Swiper re-init navigation once they mount.
  const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const images = galleryImages ?? [];
  const media = buildMedia(images, videos ?? []);
  const hasMultipleMedia = media.length > 1;

  // Swipe the thumbnail strip so the active thumbnail stays in view when the main
  // media changes via the arrows.
  useEffect(() => {
    thumbsSwiper?.slideTo(activeIndex);
  }, [thumbsSwiper, activeIndex]);

  const goToMedia = (index: number) => {
    // speed 0 → swap the main media instantly (no slide transition) on thumbnail click
    mainSwiper?.slideTo(index, 0);
    setActiveIndex(index);
    // Move keyboard focus to the selected thumbnail so the arrow-key nav works right after a click
    // (clicking a button doesn't focus it on macOS Safari/Firefox).
    thumbnailRefs.current[index]?.focus();
  };

  // Roving-tabindex keyboard nav: once a thumbnail is focused, Left/Right (and Home/End) move the
  // selection (and the main media) and shift focus to the newly-selected thumbnail — so keyboard
  // users don't have to Tab through every thumbnail. `currentIndex` is the focused thumbnail's own
  // index, which stays correct even when a key is held (focus advances synchronously).
  const handleThumbnailKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) => {
    const lastIndex = media.length - 1;
    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = Math.min(currentIndex + 1, lastIndex);
        break;
      case 'ArrowLeft':
        nextIndex = Math.max(currentIndex - 1, 0);
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    goToMedia(nextIndex);
  };

  const thumbnailLabel = (item: GalleryMedia, index: number) => {
    if (item.kind === 'video') {
      return item.video.title ?? t('viewVideo', { number: index - images.length + 1 });
    }

    return t('viewImage', { number: index + 1 });
  };

  return (
    <div>
      <Swiper
        slidesPerView={1}
        spaceBetween={10}
        modules={[Navigation]}
        navigation
        onSwiper={setMainSwiper}
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        className="product-image-slider mt-14 h-xs w-full md:h-md"
      >
        {media.map((item, index) => (
          <SwiperSlide key={item.key}>
            {item.kind === 'image' ? (
              <GalleryImageSlide image={item.image} isActive={index === activeIndex} />
            ) : (
              <div className="mx-auto h-full w-full max-w-3xl px-4 lg:py-5">
                <GalleryVideo video={item.video} isActive={index === activeIndex} />
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      {hasMultipleMedia && (
        <div className="product-image-thumbs-wrap relative mt-4">
          <Swiper
            modules={[FreeMode, Navigation]}
            freeMode
            slidesPerView="auto"
            spaceBetween={8}
            slidesOffsetBefore={24}
            slidesOffsetAfter={24}
            navigation={{ prevEl, nextEl }}
            onSwiper={setThumbsSwiper}
            className="product-image-thumbs"
          >
            {media.map((item, index) => (
              <SwiperSlide key={item.key} className="!w-auto">
                <button
                  type="button"
                  ref={(el) => {
                    thumbnailRefs.current[index] = el;
                  }}
                  onClick={() => goToMedia(index)}
                  onKeyDown={(event) => handleThumbnailKeyDown(event, index)}
                  tabIndex={index === activeIndex ? 0 : -1}
                  aria-label={thumbnailLabel(item, index)}
                  aria-current={index === activeIndex}
                  className={clsx(
                    'relative block rounded-lg border-2 transition-opacity',
                    index === activeIndex
                      ? 'border-primary-100 opacity-100'
                      : 'border-transparent opacity-60 hover:opacity-100',
                  )}
                >
                  {item.kind === 'image' || item.video.thumbnails?.[0] ? (
                    <OptimizedImage
                      image={resizedImage(
                        item.kind === 'image' ? item.image : item.video.thumbnails?.[0],
                        ImageSize.S100,
                      )}
                      sizes="80px"
                      width={80}
                      height={80}
                      className="h-16 w-16 rounded-md object-contain"
                    />
                  ) : (
                    <div
                      data-testid="video-thumbnail-placeholder"
                      className="h-16 w-16 rounded-md bg-neutral-5"
                      aria-hidden="true"
                    />
                  )}
                  {item.kind === 'video' && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <PlayIcon className="h-6 w-6 text-white drop-shadow-md" aria-hidden="true" />
                    </span>
                  )}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>

          <button
            ref={setPrevEl}
            type="button"
            aria-label={t('previousThumbnails')}
            className="thumb-nav thumb-nav-prev"
          >
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            ref={setNextEl}
            type="button"
            aria-label={t('nextThumbnails')}
            className="thumb-nav thumb-nav-next"
          >
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
